import { EFinancingCompany, EFinancingProgram } from '@if/core/types'
import { formatCurrencyNoDecimals, formatDate } from '@if/core/utils'
import { Link } from '@mui/material'
import { GridColDef } from '@mui/x-data-grid'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ELoanStatus } from '@src/types/constants'
import { LoanDashboardEntry } from './loan-filters-schema'

export const useLoanListColumns = (): GridColDef<LoanDashboardEntry>[] => {
  const { t } = useTranslation()
  return useMemo<GridColDef<LoanDashboardEntry>[]>(
    () => [
      {
        field: 'loanId',
        headerName: t('loan.loanList.loanId'),
        minWidth: 110,
        sortable: false,
      },
      {
        field: 'contractNumber',
        headerName: t('loan.loanList.contractNumber'),
        minWidth: 170,
        sortable: false,
        renderCell: (params) => {
          return (
            <Link href={`/loans/${params.row.financingProgramId}/${params.row.loanId}/calendar`}>
              {params.row.contractNumber}
            </Link>
          )
        },
      },
      {
        field: 'creditReferenceNumber',
        headerName: t('loan.loanList.creditReferenceNumber'),
        minWidth: 140,
        sortable: false,
      },
      {
        field: 'financingProgramId',
        headerName: t('loan.loanList.financingProgram'),
        minWidth: 210,
        sortable: false,
        valueFormatter: (financingProgram: EFinancingProgram) => {
          return t(`EFinancingProgram.${financingProgram}`, { ns: 'enum' })
        },
      },
      {
        field: 'financingCompanyId',
        headerName: t('loan.loanList.financingCompany'),
        flex: 1,
        minWidth: 210,
        sortable: false,
        valueFormatter: (financingCompany: EFinancingCompany) => {
          switch (financingCompany) {
            case EFinancingCompany.IFinance:
            case EFinancingCompany.Iceberg:
              return financingCompany
            default:
              return ''
          }
        },
      },
      {
        field: 'borrowerFullName',
        headerName: t('loan.loanList.borrower'),
        flex: 1,
        sortable: false,
      },
      {
        field: 'loanStatusId',
        headerName: t('loan.loanList.status'),
        flex: 1,
        sortable: false,
        valueFormatter: (loanStatusId: ELoanStatus) => t(`loan.statuses.${loanStatusId}`),
      },
      {
        field: 'createdOn',
        headerName: t('loan.loanList.createdOn'),
        sortable: false,
        minWidth: 130,
        valueFormatter: (createdOn: Date) => {
          if (createdOn.toString() === '0001-01-01T00:00:00') return ''
          return formatDate(createdOn)
        },
      },
      {
        field: 'loanAmount',
        headerName: t('loan.loanList.loanAmount'),
        minWidth: 150,
        sortable: false,
        valueFormatter: (loanAmount) => {
          return formatCurrencyNoDecimals(loanAmount ?? 0)
        },
      },
      {
        field: 'loanBalance',
        headerName: t('loan.loanList.loanBalance'),
        minWidth: 200,
        sortable: false,
        valueFormatter: (loanBalance) => {
          return formatCurrencyNoDecimals(loanBalance ?? 0)
        },
      },
    ],
    [t],
  )
}
